import { useState, useEffect } from 'react';
import { useWeather } from '../hooks/useWeather';
import { Button } from './ui/Button';
import type { WeatherInsight, PackingRecommendation } from '../types/weather';
import type { Location } from '../types/roadTrip';

interface WeatherWidgetProps {
  location?: string;
  coordinates?: { lat: number; lng: number };
  showForecast?: boolean;
  compact?: boolean;
}

export default function WeatherWidget({
  location = 'Paris, France',
  coordinates,
  showForecast = true,
  compact = false
}: WeatherWidgetProps) {
  const {
    currentWeather, forecast, loading, error, fetchWeather,
    getPackingRecommendations, getWeatherInsights, getTripSummary
  } = useWeather({ days: 7 });

  // Fetch weather on mount
  const handleFetchWeather = async () => {
    if (coordinates) {
      await fetchWeather(coordinates.lat, coordinates.lng, location);
    } else {
      // Use default coordinates for demo
      await fetchWeather(48.8566, 2.3522, location);
    }
  };

  const getWeatherIcon = (condition: string) => {
    const c = condition.toLowerCase();
    if (c.includes('sun') || c.includes('clear')) return '☀️';
    if (c.includes('cloud')) return '☁️';
    if (c.includes('rain')) return '🌧️';
    if (c.includes('snow')) return '❄️';
    if (c.includes('thunder') || c.includes('storm')) return '⛈️';
    if (c.includes('fog') || c.includes('mist')) return '🌫️';
    return '🌤️';
  };

  const getConditionColor = (condition: string) => {
    const c = condition.toLowerCase();
    if (c.includes('sun') || c.includes('clear')) return 'bg-amber-100 text-amber-800';
    if (c.includes('cloud')) return 'bg-gray-100 text-gray-800';
    if (c.includes('rain')) return 'bg-blue-100 text-blue-800';
    if (c.includes('snow')) return 'bg-cyan-100 text-cyan-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Initial load
  useState(() => {
    handleFetchWeather();
  });

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-16 bg-gray-200 rounded"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error && !currentWeather) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <div className="text-4xl mb-4">🌤️</div>
        <p className="text-gray-500 mb-4">Unable to load weather</p>
        <Button variant="primary" onClick={handleFetchWeather}>
          Try Again
        </Button>
      </div>
    );
  }

  if (!currentWeather) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <div className="text-4xl mb-4">🌤️</div>
        <p className="text-gray-500 mb-4">Click to load weather for {location}</p>
        <Button variant="primary" onClick={handleFetchWeather}>
          Load Weather
        </Button>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-4 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-80">{location}</p>
            <p className="text-3xl font-bold">{currentWeather.temperature}°C</p>
            <p className="text-sm">{currentWeather.condition}</p>
          </div>
          <div className="text-5xl">{getWeatherIcon(currentWeather.condition)}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Current Weather */}
      <div className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm opacity-80 mb-1">{location}</p>
            <p className="text-sm opacity-60">
              {new Date(currentWeather.date).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'short',
                day: 'numeric'
              })}
            </p>
          </div>
          <button
            onClick={handleFetchWeather}
            className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
            title="Refresh"
          >
            🔄
          </button>
        </div>

        <div className="flex items-center mt-4">
          <div className="text-6xl">{getWeatherIcon(currentWeather.condition)}</div>
          <div className="ml-4">
            <p className="text-5xl font-bold">{currentWeather.temperature}°C</p>
            <p className="text-lg opacity-90">{currentWeather.condition}</p>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mt-6 pt-4 border-t border-white/20">
          <div className="text-center">
            <p className="text-sm opacity-60">Humidity</p>
            <p className="font-medium">{currentWeather.humidity}%</p>
          </div>
          <div className="text-center">
            <p className="text-sm opacity-60">Wind</p>
            <p className="font-medium">{currentWeather.windSpeed} km/h</p>
          </div>
          <div className="text-center">
            <p className="text-sm opacity-60">UV Index</p>
            <p className="font-medium">{currentWeather.uvIndex}</p>
          </div>
          <div className="text-center">
            <p className="text-sm opacity-60">Precipitation</p>
            <p className="font-medium">{currentWeather.precipitation} mm</p>
          </div>
        </div>
      </div>

      {/* Forecast */}
      {showForecast && forecast && forecast.daily.length > 0 && (
        <div className="p-4 border-t">
          <h3 className="font-semibold mb-3">7-Day Forecast</h3>
          <div className="grid grid-cols-7 gap-2">
            {forecast.daily.slice(0, 7).map((day: { date: string; condition: string; temperature: number; temperatureMin: number }, index: number) => (
              <div key={index} className="text-center">
                <p className="text-xs text-gray-500 mb-1">
                  {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                </p>
                <div className="text-2xl mb-1">{getWeatherIcon(day.condition)}</div>
                <p className="font-medium text-sm">{day.temperature}°</p>
                <p className="text-xs text-gray-400">{day.temperatureMin}°</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insights */}
      {showForecast && (() => {
        const insights = getWeatherInsights();
        const recommendations = getPackingRecommendations();
        const summary = getTripSummary();

        return (
          <div className="p-4 border-t space-y-4">
            {/* Trip Summary */}
            {summary && (
              <div className={`p-3 rounded-lg ${
                summary.overallCondition === 'excellent' ? 'bg-green-100' :
                summary.overallCondition === 'good' ? 'bg-blue-100' :
                'bg-yellow-100'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">📊</span>
                  <span className="font-medium">Trip Weather Summary</span>
                  <span className={`ml-auto text-xs px-2 py-1 rounded-full ${
                    summary.overallCondition === 'excellent' ? 'bg-green-200 text-green-800' :
                    summary.overallCondition === 'good' ? 'bg-blue-200 text-blue-800' :
                    'bg-yellow-200 text-yellow-800'
                  }`}>
                    {summary.overallCondition}
                  </span>
                </div>
                <p className="text-sm">
                  Avg: {summary.averageTemperature.toFixed(1)}°C |
                  Rain: {summary.rainDays} days
                </p>
              </div>
            )}

            {/* Weather Alerts */}
            {insights.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Weather Alerts</h4>
                <div className="space-y-2">
                  {insights.slice(0, 2).map((insight: WeatherInsight, index: number) => (
                    <div key={index} className={`p-2 rounded text-sm ${
                      insight.severity === 'high' ? 'bg-red-100 text-red-800' :
                      insight.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      <span className="font-medium">{insight.title}</span>
                      <p className="text-xs mt-1 opacity-80">{insight.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Packing Recommendations */}
            {recommendations.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">🎒 Packing Recommendations</h4>
                <div className="flex flex-wrap gap-2">
                  {recommendations.slice(0, 5).map((rec: PackingRecommendation, index: number) => (
                    <span
                      key={index}
                      className={`text-xs px-2 py-1 rounded-full ${
                        rec.priority === 'essential' ? 'bg-red-100 text-red-700' :
                        rec.priority === 'recommended' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {rec.item}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
